/**
 * Slider - Base slider component
 *
 * A modern slider component using @wandb/weave/components Slider:
 * - Separated NumberInput and Slider (not bundled together)
 * - Real-time snapping during drag
 * - Optional keyboard bindings (can be disabled)
 * - Number input with +/- buttons
 * - Customizable styling (teal track and thumb via Tailwind)
 * - Configurable layout (horizontal or vertical)
 *
 * This component does NOT handle:
 * - Card/popover UI (see GenericSliderDropdown)
 * - Drag-to-increment on label (see GenericSliderDropdown)
 */

import {InfoTooltip} from '@wandb/components';
import NumberInput from '@wandb/weave/common/components/elements/NumberInput';
import {SliderKeyboardOperation} from '@wandb/weave/common/util/media';
import {Slider as WandbSlider} from '@wandb/weave/components';
import {clamp, isEmpty} from 'lodash';
import React, {useCallback, useEffect, useMemo, useRef} from 'react';
import {Popup} from 'semantic-ui-react';
import {twMerge} from 'tailwind-merge';

import {changeSliderValue, getIndexFromSliderValue} from '../utils/stepUtils';
import {SliderChangeSource, type SliderProps} from './types';

export type {SliderProps};

// Empty array constant to avoid creating new array on each render
const EMPTY_VALUES: number[] = [];

/**
 * Renders a generic slider control with label and tooltip.
 *
 * @param props - SliderProps
 * @returns React element
 */
export const Slider: React.FC<SliderProps> = ({
  min,
  max,
  current,
  values = EMPTY_VALUES,
  label = 'Step',
  showLabel = true,
  showInput = true,
  strideLength = 1,
  onChange,
  className,
  layout = 'horizontal',
  maxWidth = '100%',
  'data-test': dataTest = 'slider',
  minLabel = '',
  maxLabel = '',
  tooltipContent,
  tooltipDisabled = false,
  infoTooltip,
  snapToExistingValue = false,
  keyboardBindings,
}) => {
  // Determine layout classes
  const isVertical = layout === 'vertical';
  const showTooltip = !tooltipDisabled && !!tooltipContent;
  const sliderRef = useRef<HTMLDivElement>(null);

  // Calculate slider value based on whether we're snapping to existing values
  const sliderValue = useMemo(() => {
    if (snapToExistingValue && values.length > 0) {
      return values.indexOf(current);
    }
    return current;
  }, [snapToExistingValue, values, current]);

  // Calculate slider min/max based on whether we're using ticks
  const sliderMin = snapToExistingValue && values.length > 0 ? 0 : min;
  const sliderMax =
    snapToExistingValue && values.length > 0 ? values.length - 1 : max;
  // Radix `step` must stay 1: in snap mode the slider value is an index into
  // `values`, and a larger step would also quantize pointer drags. Stride is
  // applied by intercepting arrow keys on the thumb instead (see
  // handleThumbKeyDown).
  const radixSliderStep = 1;

  const handleSliderChange = useCallback(
    ([newVal]: number[]) => {
      if (snapToExistingValue && values.length > 0) {
        onChange(values[newVal], SliderChangeSource.Slider);
      } else {
        const clampedValue = clamp(newVal, min, max);
        onChange(clampedValue, SliderChangeSource.Slider);
      }
    },
    [snapToExistingValue, values, min, max, onChange]
  );

  const handleNumberInputChange = useCallback(
    (newVal: number | undefined) => {
      if (newVal !== undefined) {
        if (snapToExistingValue && values.length > 0) {
          // `NumberInput` receives `ticks` when snapping (see the render
          // below), so its stepper and ArrowUp/Down emit exact tick values
          // rather than `value +/- stride`; an exact match therefore covers
          // both stepping and typing a valid value.
          const exactIndex = values.indexOf(newVal);
          if (exactIndex !== -1) {
            onChange(newVal, SliderChangeSource.Input);
            return;
          }

          // Typed value not in array: try to find closest tick
          const newIdx = getIndexFromSliderValue(
            newVal,
            current,
            values,
            strideLength
          );
          if (newIdx !== undefined) {
            onChange(values[newIdx], SliderChangeSource.Input);
            return;
          }
        }
        const clampedValue = clamp(newVal, min, max);
        onChange(clampedValue, SliderChangeSource.Input);
      }
    },
    [snapToExistingValue, values, current, strideLength, min, max, onChange]
  );

  // Keyboard handling
  const isFormField = useCallback((node?: Element | null): boolean => {
    if (!node || node.nodeType !== Node.ELEMENT_NODE) {
      return false;
    }
    const tagName = node.tagName.toLowerCase();
    return ['input', 'textarea', 'select'].includes(tagName);
  }, []);

  const keyboardOperations = useMemo(() => {
    return {
      [SliderKeyboardOperation.INCREMENT]: () => {
        const newValue = changeSliderValue(current, 1, {
          min,
          max,
          availableValues: values,
          snapToAvailableValues: snapToExistingValue,
          stride: strideLength,
        });
        onChange(newValue, SliderChangeSource.Keyboard);
      },
      [SliderKeyboardOperation.DECREMENT]: () => {
        const newValue = changeSliderValue(current, -1, {
          min,
          max,
          availableValues: values,
          snapToAvailableValues: snapToExistingValue,
          stride: strideLength,
        });
        onChange(newValue, SliderChangeSource.Keyboard);
      },
    };
  }, [current, values, strideLength, min, max, onChange, snapToExistingValue]);

  // Radix moves the thumb by `step` on arrow keys, which ignores the stride.
  // Handle plain arrow keys ourselves; Home/End/PageUp/PageDown and
  // Shift+Arrow (Radix's 10-step skip) stay with Radix.
  const handleThumbKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLSpanElement>) => {
      // Modified arrows belong to the global bindings (e.g. Meta+ArrowRight)
      // or to Radix's skip behavior (Shift+Arrow)
      if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) {
        return;
      }

      let operation: SliderKeyboardOperation;
      if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
        operation = SliderKeyboardOperation.INCREMENT;
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
        operation = SliderKeyboardOperation.DECREMENT;
      } else {
        return;
      }

      // Stop Radix from applying its own single-step move on top of ours
      event.preventDefault();
      event.stopPropagation();
      keyboardOperations[operation]();
    },
    [keyboardOperations]
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (isEmpty(keyboardBindings) || isFormField(document.activeElement)) {
        return;
      }

      // Build the key combination string based on modifiers
      const modifiers = [];
      if (event.metaKey) {
        modifiers.push('Meta');
      }
      if (event.ctrlKey) {
        modifiers.push('Control');
      }
      if (event.altKey) {
        modifiers.push('Alt');
      }

      const keyCombo = [...modifiers, event.key].join('+');
      const operation = keyboardBindings?.[keyCombo];

      if (operation && operation in keyboardOperations) {
        event.preventDefault();
        event.stopPropagation();
        const handler = keyboardOperations[operation];
        return handler();
      }
    },
    [keyboardOperations, keyboardBindings, isFormField]
  );

  useEffect(() => {
    if (!isEmpty(keyboardBindings)) {
      // TODO: migrate configurable slider shortcuts to the inputSystem
      // oxlint-disable-next-line wandb/no-ad-hoc-keydown-listener
      document.addEventListener('keydown', handleKeyDown, true);
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [keyboardBindings, handleKeyDown]);

  // Label + Slider row
  const labelSliderRow = (
    <>
      {showLabel && (
        <div
          className={twMerge(
            'flex-shrink flex-grow-0 min-w-[30px] flex items-center justify-center gap-4',
            isVertical && 'w-full'
          )}>
          {showTooltip ? (
            <Popup
              trigger={
                <label
                  className="flex items-center justify-center text-sm truncate"
                  data-test={`${dataTest}-label`}>
                  {label}
                </label>
              }
              popperModifiers={{
                preventOverflow: {
                  boundariesElement: 'viewport',
                },
              }}
              content={tooltipContent}
            />
          ) : (
            <label
              className="flex items-center justify-center text-sm truncate"
              data-test={`${dataTest}-label`}>
              {label}
            </label>
          )}
          {infoTooltip && (
            <InfoTooltip
              id={`${dataTest}-info-tooltip`}
              aria-label="More info"
              size="sm">
              {infoTooltip}
            </InfoTooltip>
          )}
        </div>
      )}

      <div
        className={twMerge(
          'min-w-[80px] flex-[2_2_0%]',
          isVertical && 'w-full'
        )}
        data-test={`${dataTest}-slider`}>
        <div className="flex min-w-0 items-center gap-2">
          {minLabel && (
            <label className="text-xs text-[light-dark(var(--moon-600),var(--moon-400))] flex-shrink-0">
              {minLabel}
            </label>
          )}
          <div className="relative min-w-0 flex-1" ref={sliderRef}>
            <WandbSlider.Root
              className="relative flex items-center w-full min-w-[50px] h-32"
              name={label.toLowerCase()}
              min={sliderMin}
              max={sliderMax}
              step={radixSliderStep}
              orientation="horizontal"
              onValueChange={handleSliderChange}
              value={[sliderValue]}>
              <WandbSlider.Track className="relative flex-1 h-4 bg-[light-dark(var(--moon-250),var(--moon-700))] rounded-sm">
                <WandbSlider.Range className="absolute h-full bg-wb-accent rounded-sm" />
              </WandbSlider.Track>
              <WandbSlider.Thumb
                className={twMerge(
                  'block h-14 w-14 bg-wb-accent rounded-full border-transparent',
                  'focus:ring-2 focus:ring-wb-accent active:ring-2 active:ring-wb-accent',
                  'hover:ring-2 hover:ring-wb-accent-hover',
                  'focus:border-[2px] focus:border-white'
                )}
                onKeyDown={handleThumbKeyDown}
                onPointerUp={() => {
                  if (document.activeElement instanceof HTMLElement) {
                    document.activeElement.blur();
                  }
                }}
              />
            </WandbSlider.Root>
          </div>
          {maxLabel && (
            <label className="text-xs text-[light-dark(var(--moon-600),var(--moon-400))] flex-shrink-0">
              {maxLabel}
            </label>
          )}
        </div>
      </div>
    </>
  );

  // Number input
  const numberInputRow = (
    <div className={twMerge('flex-1', isVertical && 'w-full')}>
      <NumberInput
        className="w-full px-2 text-sm [&>input]:!px-1"
        value={current}
        onChange={handleNumberInputChange}
        stepper={true}
        useStepperPlusMinus={true}
        strideLength={strideLength}
        min={min}
        max={max}
        ticks={snapToExistingValue ? values : undefined}
      />
    </div>
  );

  return (
    <div
      className={twMerge(
        'flex flex-row items-center justify-center w-full',
        maxWidth === '100%' ? 'max-w-full' : `max-w-[${maxWidth}]`,
        className
      )}
      style={maxWidth !== '100%' ? {maxWidth} : undefined}
      data-test={dataTest}>
      <div
        className={twMerge(
          'flex items-center gap-12 w-full',
          isVertical ? 'flex-col-reverse' : 'flex-row'
        )}>
        {labelSliderRow}
        {showInput && numberInputRow}
      </div>
    </div>
  );
};
